import { Box, Button, CircularProgress } from '@mui/material'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierType,
  CarrierVisitDirection,
  CreateGeneralCargoOrderCommand,
  OrderStatus,
  RailVisitResponseDto,
  TruckVisitDto,
  UpdateGeneralCargoOrderCommand,
  VesselVisitDto,
} from '@planning/app/api'
import generalCargoService from '@planning/services/generalCargoService'
import { ValidateOutboundOrderFunc } from '@planning/stores/gateControl/ValidateOutboundDto'
import { useTranslate } from '@tolgee/react'
import { Header, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import {
  CreateGeneralCargoOrderPageForm,
  ICreateGeneralCargoOrderFormData,
} from './components/CreateGeneralCargoOrderPageForm'

interface Props {
  carrierVisit?: VesselVisitDto | TruckVisitDto | RailVisitResponseDto
  carrierVisitType?: CarrierType
  disableCarrierVisitSelection?: boolean
  isTruckAppointment?: boolean
  forcedDirection?: CarrierVisitDirection
  renderDamage?: (orderId: number) => React.ReactElement
  validateOutboundRequest?: ValidateOutboundOrderFunc
  onCreated?: (orderId: number) => void | Promise<void>
  onClose?: () => void
  variant?: 'dialog' | 'page'
}

export const CreateGeneralCargoOrder: FC<Props> = observer(
  ({
    carrierVisit,
    carrierVisitType,
    disableCarrierVisitSelection,
    isTruckAppointment,
    forcedDirection,
    renderDamage,
    validateOutboundRequest,
    onCreated,
    onClose,
    variant = 'dialog',
  }) => {
    const { generalCargoViewStore, appViewStore, dialogStore } = usePlanningStore()

    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const isSubmitting = appViewStore.isLoading

    const handleClose = () => {
      if (onClose) {
        onClose()
        return
      }

      dialogStore.close()
    }

    const isAllowGeneralCargoOrderToChangeStatusFeatureEnabled = useBooleanFlagValue(
      'gc-update-order-status',
      false,
    )

    const validateGeneralCargoOrderForm = async (data: ICreateGeneralCargoOrderFormData) => {
      if (data.status === OrderStatus.Fulfilled) {
        if (data.actualCargoAmount === 0) {
          appViewStore
            .setOpenConfirmDialog(
              true,
              t(
                'actualCargoQuantityIsZeroConfirmation',
                'Actual cargo quantity is 0. Do you want to continue?',
              ),
            )
            .then(confirmed => confirmed && setOrderFormDataAndSubmit(data))
          return
        } else if (!data.actualCargoAmount) {
          appViewStore
            .setOpenConfirmDialog(
              true,
              t(
                'actualCargoQuantityIsNotSetConfirmation',
                'Actual cargo quantity is not set. Do you want to continue? (Actual quantity will be set to 0)',
              ),
            )
            .then(confirmed => confirmed && setOrderFormDataAndSubmit(data, 0))
          return
        } else if (data.actualCargoAmount !== data.plannedCargoAmount) {
          appViewStore
            .setOpenConfirmDialog(
              true,
              t(
                'actualCargoQuantityIsDifferentConfirmation',
                'Actual cargo quantity is different from planned. Do you want to continue?',
              ),
            )
            .then(confirmed => confirmed && setOrderFormDataAndSubmit(data))
          return
        }
      }

      setOrderFormDataAndSubmit(data)
    }

    const setOrderFormDataAndSubmit = (
      data: ICreateGeneralCargoOrderFormData,
      actualAmount?: number,
    ) => {
      const orderFormData =
        actualAmount || actualAmount === 0 ? { ...data, actualCargoAmount: actualAmount } : data
      generalCargoViewStore.setOrderFormData(orderFormData)
      onSubmitGeneralCargoOrderForm()
    }

    const onSubmitGeneralCargoOrderForm = async () => {
      const data = generalCargoViewStore.orderFormData
      if (!data) return

      if (appViewStore.isLoading) {
        return
      }

      appViewStore.showLoader()

      try {
        if (generalCargoViewStore.selectedOrder) {
          await onUpdateGeneralCargoOrderFormSubmit(data)
        } else {
          await onCreateGeneralCargoOrderFormSubmit(data)
        }
      } finally {
        appViewStore.hideLoader()
      }
    }

    const onCreateGeneralCargoOrderFormSubmit = async (data: ICreateGeneralCargoOrderFormData) => {
      try {
        const cmd = {
          referenceNumber: data.referenceNumber,
          direction: data.direction,
          imoClasses: data.imoClasses,
          carrierVisitType: data.carrierVisitType,
          carrierVisitId: data.carrierVisit?.id,
          railcarId: data.carrierVisitType === CarrierType.Train ? data.railcarId : null,
          railTrackId: data.carrierVisitType === CarrierType.Train ? data.railTrackId : null,
          customerId: data.customer?.id,
          billOfLading: data.billOfLading,
          lotNumber: data.lotNumber,
          commodityId: data.commodity?.id,
          plannedAmount: data.plannedCargoAmount,
          handlingInstructions: data.handlingInstructions,
          identifiableItems: data.identifiableItems,
          packageId: data.packageId,
          grossWeight: data.grossWeight,
          weightUnit: data.weightUnit,
        } as CreateGeneralCargoOrderCommand

        const createdOrderId = await generalCargoService.create(cmd)
        await onCreated?.(createdOrderId)

        handleClose()

        appViewStore.setShowAlert(
          'success',
          t('generalCargoOrderCreatedSuccessfully', 'General Cargo Order created successfully'),
        )
      } catch {
        appViewStore.setShowAlert('error', t('failedToSave', 'Failed to save'))
      }
    }

    const onUpdateGeneralCargoOrderFormSubmit = async (data: ICreateGeneralCargoOrderFormData) => {
      try {
        const cmd = {
          id: generalCargoViewStore.selectedOrder!.id,
          status: data.status,
          referenceNumber: data.referenceNumber,
          direction: data.direction,
          imoClasses: data.imoClasses,
          carrierVisitType: data.carrierVisitType,
          carrierVisitId: data.carrierVisit?.id,
          railcarId: data.carrierVisitType === CarrierType.Train ? data.railcarId : null,
          railTrackId: data.carrierVisitType === CarrierType.Train ? data.railTrackId : null,
          customerId: data.customer?.id,
          billOfLading: data.billOfLading,
          lotNumber: data.lotNumber,
          commodityId: data.commodity?.id,
          plannedAmount: data.plannedCargoAmount,
          actualAmount: data.actualCargoAmount,
          handlingInstructions: data.handlingInstructions,
          identifiableItems: data.identifiableItems,
          packageId: data.packageId,
          grossWeight: data.grossWeight,
          weightUnit: data.weightUnit,
        } as UpdateGeneralCargoOrderCommand

        await generalCargoService.update(generalCargoViewStore.selectedOrder!.id, cmd)

        generalCargoViewStore.selectOrder()

        handleClose()
        appViewStore.setShowAlert(
          'success',
          t('generalCargoOrderUpdatedSuccessfully', 'General Cargo Order updated successfully'),
        )
      } catch {
        appViewStore.setShowAlert('error', t('failedToSave', 'Failed to save'))
      }
    }

    const title = generalCargoViewStore.selectedOrder
      ? t('editGeneralCargoOrder', 'Edit general cargo order')
      : t('createGeneralCargoOrder', 'Create general cargo order')

    const formId = generalCargoViewStore.selectedOrder ? 'gc-order-edit-form' : 'gc-order-form'

    const formContent = (
      <Box paddingX={theme.customSpacing.l}>
        <CreateGeneralCargoOrderPageForm
          formId={formId}
          store={generalCargoViewStore}
          carrierVisit={carrierVisit}
          carrierVisitType={carrierVisitType}
          disableCarrierVisitSelection={disableCarrierVisitSelection}
          isTruckAppointment={isTruckAppointment}
          forcedDirection={forcedDirection}
          onSubmit={validateGeneralCargoOrderForm}
          renderDamage={renderDamage}
          validateOutboundRequest={validateOutboundRequest}
        />
      </Box>
    )

    return (
      <>
        {variant === 'page' ? (
          <>{formContent}</>
        ) : (
          <Box sx={{ bgcolor: theme.palette.grey[100], minHeight: '100vh' }}>
            <Header
              title={title}
              rightRenderOption={
                <>
                  <Button
                    color='inherit'
                    onClick={() => {
                      generalCargoViewStore.selectOrder()
                      handleClose()
                    }}
                    variant='text'
                  >
                    {t('cancel', 'Cancel')}
                  </Button>
                  {(isAllowGeneralCargoOrderToChangeStatusFeatureEnabled ||
                    generalCargoViewStore.selectedOrder?.status !== OrderStatus.Fulfilled) && (
                    <Button
                      type='submit'
                      form={formId}
                      color='primary'
                      variant='contained'
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size='1rem' /> : undefined}
                    >
                      {t('submit', 'Submit')}
                    </Button>
                  )}
                </>
              }
            />

            <Box sx={{ paddingTop: theme.customSpacing.l, paddingBottom: theme.customSpacing.l }}>
              {formContent}
            </Box>
          </Box>
        )}
      </>
    )
  },
)
